import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Row, Col, Modal } from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, useMapEvents, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './HR.css';

// Réparer l'icône par défaut de Leaflet qui casse avec Webpack/React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

function getWeekStr(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getFullYear()}-W${String(weekNo).padStart(2,'0')}`;
}

export default function EmployeesSchedule() {
  const user = useSelector(s => s.AuthReducer?.user);
  const token = localStorage.getItem('token');
  const headers = { headers: { Authorised: token } };

  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedWeek, setSelectedWeek] = useState(getWeekStr());
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // { employee }
  const [shifts, setShifts] = useState(Array(7).fill(null).map((_, i) => ({ dayOfWeek: i, startTime: '09:00', endTime: '17:00', isOff: false })));
  const [saving, setSaving] = useState(false);
  const [showGpsModal, setShowGpsModal] = useState(false);
  const [gpsTarget, setGpsTarget] = useState(null);
  const [gpsForm, setGpsForm] = useState({ lat: '', lng: '', radiusMeters: 200, requireBreak: false });
  const [detecting, setDetecting] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async () => {
    try {
      const [locsRes, empsRes, schedsRes] = await Promise.all([
        axios.get('/api/hr/locations', headers),
        axios.get(`/api/hr/employees${selectedLocation ? `?locationId=${selectedLocation}` : ''}`, headers),
        axios.get(`/api/hr/schedule?week=${selectedWeek}${selectedLocation ? `&locationId=${selectedLocation}` : ''}`, headers)
      ]);
      setLocations(locsRes.data);
      setEmployees(empsRes.data);
      setSchedules(schedsRes.data);
    } catch { /* silent */ }
  }, [selectedLocation, selectedWeek]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getScheduleForEmployee = (empId) =>
    schedules.find(s => s.employee?._id === empId || s.employee === empId);

  const openEdit = (emp) => {
    const existing = getScheduleForEmployee(emp._id);
    setEditTarget(emp);
    setShifts(Array(7).fill(null).map((_, i) => {
      const s = existing?.shifts?.find(sh => sh.dayOfWeek === i);
      return s ? { ...s } : { dayOfWeek: i, startTime: '09:00', endTime: '17:00', isOff: false };
    }));
    setShowModal(true);
  };

  const updateShift = (dayIdx, field, value) => {
    setShifts(prev => prev.map((s, i) => i === dayIdx ? { ...s, [field]: value } : s));
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      // Utiliser la localisation sélectionnée ou la première de l'employé
      const locId = selectedLocation || (Array.isArray(editTarget?.assignedLocations) && editTarget.assignedLocations[0]?._id) || (editTarget?.assignedLocations?._id);
      
      if (!locId) {
        alert("Veuillez sélectionner une localisation pour cet employé.");
        setSaving(false);
        return;
      }

      await axios.post('/api/hr/schedule', {
        employeeId: editTarget._id,
        locationId: locId,
        week: selectedWeek,
        shifts
      }, { headers: { Authorised: token } });
      setShowModal(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };


  const handleAddLocation = async (employeeId, locationId) => {
    if (!locationId) return;
    try {
      await axios.put(`/api/hr/employees/${employeeId}/location`, { locationId }, headers);
      fetchData();
    } catch (err) {
      alert('Erreur ajout localisation');
    }
  };

  const handleRemoveLocation = async (employeeId, locationId) => {
    try {
      await axios.delete(`/api/hr/employees/${employeeId}/location`, { 
        data: { locationId },
        ...headers 
      });
      fetchData();
    } catch (err) {
      alert('Erreur suppression localisation');
    }
  };

  const openGpsModal = (loc) => {
    setGpsTarget(loc);
    setGpsForm({ 
      lat: loc.gps?.lat || '', 
      lng: loc.gps?.lng || '', 
      radiusMeters: loc.radiusMeters || 20,
      requireBreak: !!loc.requireBreak
    });
    setShowGpsModal(true);
  };

  const detectMyGps = () => {
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      pos => { setGpsForm(f => ({ ...f, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) })); setDetecting(false); },
      () => { alert('GPS non disponible'); setDetecting(false); }
    );
  };

  const saveGps = async () => {
    try {
      await axios.put(`/api/hr/locations/${gpsTarget._id}/gps`, gpsForm, { headers: { Authorised: token } });
      setShowGpsModal(false);
      fetchData();
    } catch { alert('Erreur sauvegarde GPS'); }
  };

  // Composant interne pour gérer les clics sur la carte
  function MapEvents() {
    useMapEvents({
      click(e) {
        setGpsForm(f => ({ ...f, lat: e.latlng.lat.toFixed(6), lng: e.latlng.lng.toFixed(6) }));
      },
    });
    return null;
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'useradmin';

  return (
    <div className="hr-page">
      <div className="hr-header">
        <h1>📅 Emploi du Temps</h1>
        <p>Gestion des plannings par localisation</p>
      </div>

      {/* Filtres */}
      <div className="hr-card" style={{ marginBottom: 24 }}>
        <Row className="align-items-end g-3">
          <Col md={4}>
            <div className="hr-form-group" style={{ marginBottom: 0 }}>
              <label>Localisation</label>
              <select className="hr-form-control" value={selectedLocation} onChange={e => setSelectedLocation(e.target.value)}>
                <option value="">Toutes les localisations</option>
                {locations.map(l => <option key={l._id} value={l._id}>{l.name}</option>)}
              </select>
            </div>
          </Col>
          <Col md={4}>
            <div className="hr-form-group" style={{ marginBottom: 0 }}>
              <label>Semaine</label>
              <input
                type="week"
                className="hr-form-control"
                value={selectedWeek}
                onChange={e => setSelectedWeek(e.target.value)}
              />
            </div>
          </Col>
          <Col md={4}>
            <button className="hr-btn hr-btn-primary" onClick={fetchData} style={{ width: '100%' }}>
              🔄 Actualiser
            </button>
          </Col>
        </Row>
      </div>

      {/* Configuration GPS des sites (admin uniquement) */}
      {isAdmin && (
        <div className="hr-card" style={{ marginBottom: 24 }}>
          <div className="hr-card-title"><span className="icon">📍</span> Configuration GPS des Sites</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {locations.map(loc => (
              <div
                key={loc._id}
                className={`location-gps-card ${loc.gps?.lat ? 'has-gps' : ''}`}
                onClick={() => openGpsModal(loc)}
                style={{ flex: '1 1 200px', minWidth: 200 }}
              >
                <span className="location-gps-icon">📍</span>
                <div className="location-gps-info">
                  <h4>{loc.name}</h4>
                  {loc.gps?.lat
                    ? <p>✅ GPS configuré — Rayon {loc.radiusMeters}m</p>
                    : <p>⚠ GPS non configuré — Cliquez pour définir</p>
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tableau Planning */}
      <div className="hr-card">
        <div className="hr-card-title"><span className="icon">👥</span> Planning des Employés — {selectedWeek}</div>
        <div className="schedule-grid">
          <table className="schedule-table">
            <thead>
              <tr>
                <th>Employé</th>
                {DAYS.map(d => <th key={d}>{d}</th>)}
                {isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: '#a0aec0', padding: 30 }}>Aucun employé trouvé</td></tr>
              ) : employees.map(emp => {
                const sched = getScheduleForEmployee(emp._id);
                return (
                  <tr key={emp._id}>
                    <td>
                      <div className="employee-row">
                        <div className="employee-avatar">{emp.name?.charAt(0)?.toUpperCase()}</div>
                        <div>
                          <div className="employee-name">{emp.name}</div>
                          <div className="employee-role">{emp.position || emp.department}</div>
                          <div className="employee-locations-list" style={{ marginTop: 6 }}>
                            {Array.isArray(emp.assignedLocations) && emp.assignedLocations.map(loc => (
                              <span key={loc._id} className="location-badge">
                                {loc.name}
                                {isAdmin && (
                                  <button 
                                    className="remove-loc-btn" 
                                    onClick={() => handleRemoveLocation(emp._id, loc._id)}
                                    title="Supprimer ce lieu"
                                  >×</button>
                                )}
                              </span>
                            ))}
                          </div>

                          {isAdmin && (
                            <select 
                              className="hr-form-control" 
                              style={{ fontSize: '0.7rem', padding: '2px 5px', height: 'auto', width: 'auto', marginTop: 4 }}
                              value=""
                              onChange={(e) => handleAddLocation(emp._id, e.target.value)}
                            >
                              <option value="">+ Ajouter lieu...</option>
                              {locations
                                .filter(loc => {
                                  const locs = Array.isArray(emp.assignedLocations) ? emp.assignedLocations : (emp.assignedLocations ? [emp.assignedLocations] : []);
                                  return !locs.some(l => (l._id || l) === loc._id);
                                })
                                .map(loc => (
                                <option key={loc._id} value={loc._id}>{loc.name}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>
                    </td>
                    {DAYS.map((_, dayIdx) => {
                      const shift = sched?.shifts?.find(s => s.dayOfWeek === dayIdx);
                      return (
                        <td key={dayIdx}>
                          <div className="shift-cell">
                            {!sched ? (
                              <span style={{ color: '#cbd5e0', fontSize: '0.78rem' }}>—</span>
                            ) : shift?.isOff ? (
                              <span className="shift-time off">Repos</span>
                            ) : shift ? (
                              <>
                                <span className="shift-time">{shift.startTime}</span>
                                <span style={{ fontSize: '0.7rem', color: '#718096' }}>→</span>
                                <span className="shift-time">{shift.endTime}</span>
                              </>
                            ) : (
                              <span style={{ color: '#cbd5e0', fontSize: '0.78rem' }}>—</span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    {isAdmin && (
                      <td>
                        <button className="hr-btn hr-btn-primary" style={{ padding: '6px 14px', fontSize: '0.8rem' }} onClick={() => openEdit(emp)}>
                          ✏ Modifier
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Edition Planning */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg,#4318FF,#6b46ff)', color: 'white' }}>
          <Modal.Title>📅 Planning — {editTarget?.name} — {selectedWeek}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 28 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ padding: '10px 14px', textAlign: 'left', color: '#718096', fontWeight: 700, fontSize: '0.8rem' }}>Jour</th>
                  <th style={{ padding: '10px 14px', color: '#718096', fontWeight: 700, fontSize: '0.8rem' }}>Repos</th>
                  <th style={{ padding: '10px 14px', color: '#718096', fontWeight: 700, fontSize: '0.8rem' }}>Arrivée</th>
                  <th style={{ padding: '10px 14px', color: '#718096', fontWeight: 700, fontSize: '0.8rem' }}>Départ</th>
                  <th style={{ padding: '10px 14px', color: '#718096', fontWeight: 700, fontSize: '0.8rem' }}>Durée</th>
                </tr>
              </thead>
              <tbody>
                {shifts.map((s, i) => {
                  const duration = !s.isOff && s.startTime && s.endTime
                    ? (() => {
                        const [sh, sm] = s.startTime.split(':').map(Number);
                        const [eh, em] = s.endTime.split(':').map(Number);
                        const mins = (eh * 60 + em) - (sh * 60 + sm);
                        return mins > 0 ? `${Math.floor(mins/60)}h${String(mins%60).padStart(2,'0')}` : '—';
                      })()
                    : '—';
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f4ff' }}>
                      <td style={{ padding: '12px 14px', fontWeight: 700, color: '#0f1535' }}>{DAYS[i]}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <input type="checkbox" checked={s.isOff} onChange={e => updateShift(i, 'isOff', e.target.checked)} style={{ width: 18, height: 18, cursor: 'pointer' }} />
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <input type="time" value={s.startTime} onChange={e => updateShift(i, 'startTime', e.target.value)} disabled={s.isOff} className="hr-form-control" style={{ maxWidth: 130 }} />
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <input type="time" value={s.endTime} onChange={e => updateShift(i, 'endTime', e.target.value)} disabled={s.isOff} className="hr-form-control" style={{ maxWidth: 130 }} />
                      </td>
                      <td style={{ padding: '12px 14px', fontWeight: 700, color: s.isOff ? '#a0aec0' : '#4318FF' }}>{s.isOff ? 'Repos' : duration}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="hr-btn hr-btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
          <button className="hr-btn hr-btn-primary" onClick={saveSchedule} disabled={saving}>
            {saving ? '⏳ Sauvegarde...' : '💾 Sauvegarder'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Modal GPS Location */}
      <Modal show={showGpsModal} onHide={() => setShowGpsModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg,#4318FF,#6b46ff)', color: 'white' }}>
          <Modal.Title>📍 GPS — {gpsTarget?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 20 }}>
          <p style={{ color: '#718096', marginBottom: 15, fontSize: '0.9rem' }}>
            Cliquez sur la carte pour définir l'emplacement exact de ce site de travail.
          </p>
          
          <div style={{ height: '350px', width: '100%', borderRadius: 12, overflow: 'hidden', marginBottom: 20, border: '2px solid #e0e7ff' }}>
            <MapContainer 
              center={[gpsForm.lat || 36.8065, gpsForm.lng || 10.1815]} 
              zoom={13} 
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {gpsForm.lat && gpsForm.lng && (
                <>
                  <Marker position={[gpsForm.lat, gpsForm.lng]} />
                  <Circle 
                    center={[gpsForm.lat, gpsForm.lng]} 
                    radius={gpsForm.radiusMeters || 20}
                    pathOptions={{ color: '#4318FF', fillColor: '#4318FF', fillOpacity: 0.2 }}
                  />
                </>
              )}
              <MapEvents />
            </MapContainer>
          </div>

          <Row>
            <Col md={4}>
              <div className="hr-form-group">
                <label>Latitude</label>
                <input className="hr-form-control" type="number" step="any" value={gpsForm.lat} onChange={e => setGpsForm(f => ({ ...f, lat: e.target.value }))} />
              </div>
            </Col>
            <Col md={4}>
              <div className="hr-form-group">
                <label>Longitude</label>
                <input className="hr-form-control" type="number" step="any" value={gpsForm.lng} onChange={e => setGpsForm(f => ({ ...f, lng: e.target.value }))} />
              </div>
            </Col>
            <Col md={4}>
              <div className="hr-form-group">
                <label>Rayon (mètres)</label>
                <input className="hr-form-control" type="number" value={gpsForm.radiusMeters} onChange={e => setGpsForm(f => ({ ...f, radiusMeters: Number(e.target.value) }))} />
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <div className="hr-form-group d-flex align-items-center gap-3" style={{ backgroundColor: '#f8f9ff', padding: '12px 16px', borderRadius: 12, border: '1px dashed #4318FF' }}>
                <input 
                  type="checkbox" 
                  id="requireBreak"
                  style={{ width: 22, height: 22, cursor: 'pointer' }}
                  checked={gpsForm.requireBreak} 
                  onChange={e => setGpsForm(f => ({ ...f, requireBreak: e.target.checked }))} 
                />
                <label htmlFor="requireBreak" style={{ marginBottom: 0, fontWeight: 700, color: '#0f1535', cursor: 'pointer' }}>
                  ☕ Pause obligatoire (4 pointages requis pour ce site)
                  <div style={{ fontWeight: 400, fontSize: '0.8rem', color: '#718096' }}>
                    L'employé doit prendre 1h de pause flexible. Tout dépassement sera pénalisé.
                  </div>
                </label>
              </div>
            </Col>
          </Row>

          <button className="hr-btn hr-btn-secondary" onClick={detectMyGps} disabled={detecting} style={{ width: '100%', marginTop: 4 }}>
            {detecting ? '⏳ Détection...' : '🎯 Utiliser ma position actuelle'}
          </button>
        </Modal.Body>
        <Modal.Footer>
          <button className="hr-btn hr-btn-secondary" onClick={() => setShowGpsModal(false)}>Annuler</button>
          <button className="hr-btn hr-btn-primary" onClick={saveGps}>💾 Sauvegarder GPS</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
